import React from "react";

export default function PMInstantCompleteModal({
  open,
  job,
  jobs = [],
  operation,
  order,
  onCancel,
  onConfirm,
}) {
  // Support both single job and multiple jobs
  const isMulti = jobs && jobs.length > 0;
  const jobList = isMulti ? jobs : (job ? [job] : []);

  if (!open || jobList.length === 0) return null;

  const operationLabel = operation?.operationCode || operation?.operation || "Unknown Operation";
  const totalQty = jobList.reduce((sum, j) => sum + (j.qty || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 px-6 py-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Confirm Completion
          </h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <p className="text-gray-700 mb-4">
            You are about to mark {isMulti ? (
              <span className="font-semibold text-emerald-600">{jobList.length} jobs</span>
            ) : (
              "the following job"
            )} as complete for <span className="font-semibold text-emerald-600">{operationLabel}</span>:
          </p>

          {/* Job Details */}
          {isMulti ? (
            /* Multi-job list */
            <div className="bg-slate-100 rounded-lg border border-gray-200 p-4 mb-4 max-h-64 overflow-y-auto">
              <div className="space-y-2">
                {jobList.map((j, idx) => (
                  <div key={j.id || idx} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm">{j.jobNumber}</div>
                      <div className="text-xs text-gray-600 truncate" title={j.name}>{j.name || "Unnamed Job"}</div>
                    </div>
                    <div className="text-amber-600 font-semibold text-sm ml-3">
                      Qty: {j.qty}
                    </div>
                  </div>
                ))}
              </div>
              {/* Summary */}
              <div className="mt-3 pt-3 border-t border-gray-300 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Total</span>
                <span className="text-amber-600 font-bold">{jobList.length} jobs · {totalQty} qty</span>
              </div>
            </div>
          ) : (
            /* Single job card */
            <div className="bg-slate-100 rounded-lg border border-gray-200 p-4 mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500 text-xs uppercase">Job Number</span>
                  <div className="font-semibold text-gray-900">{jobList[0].jobNumber}</div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs uppercase">Quantity</span>
                  <div className="font-semibold text-amber-600">{jobList[0].qty}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500 text-xs uppercase">SKU Name</span>
                  <div className="text-gray-900 truncate" title={jobList[0].name}>{jobList[0].name || "Unnamed Job"}</div>
                </div>
                {order?.quoteNumber && (
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Quote</span>
                    <div className="text-purple-600">{order.quoteNumber}</div>
                  </div>
                )}
                {order?.orderNumber && (
                  <div>
                    <span className="text-gray-500 text-xs uppercase">Order</span>
                    <div className="text-blue-600">{order.orderNumber}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="text-gray-500 text-sm">
            This will create {isMulti ? `${jobList.length} completed time tickets` : "a completed time ticket"} and sync to JobBoss.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-slate-100 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-bold transition-colors flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {isMulti ? `Complete ${jobList.length} Jobs` : "Confirm Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}
